import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { brand } from "../../../../../theme/themePrimitives";
import {
  AssessmentProgressStatus,
  AssessmentReportAttempt,
} from "../../../../../services/api/types/reports/reports";
import { formatAttemptDateRange } from "../../../../../utils/helpers";
import useScreenSize from "../../../../../hooks/useScreenSize";

const QUESTION_TABLE_MIN_WIDTH = 1219;
const SCROLL_ARROW_W = 44;

interface AssesmentReportTableProps {
  attempts: AssessmentReportAttempt[];
}

const StatusChip = ({ status }: { status: AssessmentProgressStatus }) => {
  const isPassedOrCompleted =
    status === AssessmentProgressStatus.PASSED ||
    status === AssessmentProgressStatus.COMPLETED;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.4,
        borderRadius: "16px",
        backgroundColor: isPassedOrCompleted ? "#ECFDF3" : "#FEF3F2",
        border: `1px solid ${isPassedOrCompleted ? "#ABEFC6" : "#FECDCA"}`,
        whiteSpace: "nowrap",
      }}
    >
      <Typography
        fontSize={12}
        fontWeight={500}
        color={isPassedOrCompleted ? "#067647" : "#B42318"}
        lineHeight={1}
        sx={{ textTransform: "capitalize" }}
      >
        {status}
      </Typography>
    </Box>
  );
};

interface AttemptCardProps {
  attempt: AssessmentReportAttempt;
  isExpanded: boolean;
  onToggle: () => void;
  borderColor: string;
  headerBg: string;
}

const AttemptCard: React.FC<AttemptCardProps> = ({
  attempt,
  isExpanded,
  onToggle,
  borderColor,
  headerBg,
}) => {
  const { isLatest, isFirst } = attempt;
  const { isTablet } = useScreenSize();

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startScrollLeft: number;
    trackW: number;
    thumbW: number;
  } | null>(null);
  const [scrollbar, setScrollbar] = useState({
    show: false,
    thumbWidth: 0,
    thumbLeft: 0,
  });

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) {
      setScrollbar((s) => ({ ...s, show: false }));
      return;
    }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft =
      maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || !isExpanded) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => {
      el.removeEventListener("scroll", refreshScrollbar);
      ro.disconnect();
    };
  }, [refreshScrollbar, isExpanded]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(
        0,
        Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale),
      );
      el.scrollLeft = newScrollLeft;
      if (thumbRef.current) {
        const newLeft =
          maxScroll > 0
            ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW)
            : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar();
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = {
      startX: clientX,
      startScrollLeft: el.scrollLeft,
      trackW: el.clientWidth - SCROLL_ARROW_W * 2,
      thumbW: scrollbar.thumbWidth,
    };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0px 1px 3px 0px #1018281A",
        p: 0,
      }}
    >
      {/* Attempt header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          p: isTablet ? "12px 35px 12px 16px" : "12px 64px 12px 12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box
            sx={{
              backgroundColor: isLatest ? "#F9F5FF" : "#F9FAFB",
              borderRadius: "9999px",
              border: `1px solid ${isLatest ? "#E9D7FE" : "#EAECF0"}`,
              px: 1.5,
              py: 0.4,
            }}
          >
            <Typography
              fontSize={12}
              fontWeight={500}
              color={isLatest ? brand[600] : "#344054"}
            >
              Attempt {attempt.attemptNumber}
              {isLatest ? " · Latest" : isFirst ? " · First" : ""}
            </Typography>
          </Box>
          <Stack>
            <Typography fontSize={12} color="#101828" fontWeight={500}>
              {formatAttemptDateRange(attempt.started, attempt.completed)}
            </Typography>
            <Typography fontSize={12} color="#475467">
              {attempt.timeSpent != null
                ? `${attempt.timeSpent} mins total`
                : "-"}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "center", gap: isTablet ? 2 : 21 }}
        >
          <Stack direction="row" gap={2.5}>
            <Typography fontSize={12} fontWeight={500} color="#101828">
              Score: {attempt.score == null ? "–" : attempt.score}
            </Typography>
            <StatusChip status={attempt.status} />
          </Stack>
          <Box
            onClick={onToggle}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            {isExpanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 22, color: "#475467" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 22, color: "#475467" }} />
            )}
            <Typography fontSize={12} fontWeight={500} color="#475467">
              {isExpanded ? "Hide" : "Show"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {isExpanded && (
        <>
          {/* Stats row */}
          <Box
            sx={{
              borderBottom: `1px solid ${borderColor}`,
              backgroundColor: "#fff",
              py: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              rowGap: 1.5,
              justifyContent: "space-between",
            }}
          >
            <Stack flexDirection="row">
              <Box sx={{ width: isTablet ? "auto" : 146, px: "12px" }}>
                <Typography fontSize={12} color="#667085" fontWeight={500}>
                  Started
                </Typography>
                <Typography fontSize={12} color="#101828" fontWeight={500}>
                  {attempt.started}
                </Typography>
              </Box>
              <Box sx={{ width: isTablet ? "auto" : 181, px: " 12px" }}>
                <Typography fontSize={12} color="#667085" fontWeight={500}>
                  Last Accessed
                </Typography>
                <Typography fontSize={12} color="#101828" fontWeight={500}>
                  {attempt.lastAccessed}
                </Typography>
              </Box>
              <Box sx={{ width: isTablet ? "auto" : 274, px: "12px" }}>
                <Typography fontSize={12} color="#667085" fontWeight={500}>
                  Completed
                </Typography>
                <Typography fontSize={12} color="#101828" fontWeight={500}>
                  {attempt.completed}
                </Typography>
              </Box>
            </Stack>
            <Stack flexDirection="row">
              <Box sx={{ width: isTablet ? "auto" : 275, px: "12px" }}>
                <Typography fontSize={12} color="#667085" fontWeight={500}>
                  Time spent
                </Typography>
                <Typography fontSize={12} color="#101828" fontWeight={500}>
                  {attempt.timeSpent != null
                    ? `${attempt.timeSpent} mins`
                    : "-"}
                </Typography>
              </Box>
              {!isTablet && (
                <>
                  <Box sx={{ width: 230, px: "12px" }}>
                    <Typography fontSize={12} color="#667085" fontWeight={500}>
                      Status
                    </Typography>
                    <Typography
                      fontSize={12}
                      color="#101828"
                      fontWeight={500}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {attempt.status}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 123, px: "12px" }}>
                    <Typography fontSize={12} color="#667085" fontWeight={500}>
                      Score
                    </Typography>
                    <Typography fontSize={12} color="#101828" fontWeight={500}>
                      {attempt.score == null ? "–" : attempt.score}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Box>

          {/* Question table */}
          <TableContainer
            ref={tableScrollRef}
            sx={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x pan-y",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Table
              sx={{ minWidth: QUESTION_TABLE_MIN_WIDTH, tableLayout: "fixed" }}
            >
              <TableBody>
                <TableRow
                  sx={{
                    "& td": {
                      fontSize: 12,
                      color: "#475467",
                      fontWeight: 500,
                      backgroundColor: headerBg,
                      borderBottom: `1px solid ${borderColor}`,
                      py: 1,
                    },
                  }}
                >
                  <TableCell sx={{ width: isTablet ? 145 : 135 }}></TableCell>
                  <TableCell sx={{ width: isTablet ? 160 : 174 }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ width: isTablet ? 610 : 562 }}>
                    Question
                  </TableCell>
                  <TableCell sx={{ width: 223 }}>Response</TableCell>
                  <TableCell sx={{ width: 123 }}>Result</TableCell>
                </TableRow>

                {attempt.questions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography fontSize={13} color="#475467">
                        No questions answered for this attempt.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  attempt.questions.map((q) => (
                    <TableRow
                      key={q.questionNo}
                      sx={{
                        "& td": {
                          fontSize: 12,
                          color: "#475467",
                          borderBottom: `1px solid ${borderColor}`,
                          py: 1.25,
                        },
                        "&:hover": { backgroundColor: "#F9FAFB" },
                      }}
                    >
                      <TableCell>{q.questionNo}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {q.startDateTime}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>
                        {q.question ?? "-"}
                      </TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>
                        {q.userResponse ?? "-"}
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={500}>
                          {q.result === true ? "Correct" : "Incorrect"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {scrollbar.show && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: SCROLL_ARROW_W,
                backgroundColor: "#fff",
                userSelect: "none",
              }}
            >
              <Box
                sx={{
                  width: SCROLL_ARROW_W,
                  height: SCROLL_ARROW_W,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                onMouseDown={() => scrollByAmount(-100)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  scrollByAmount(-100);
                }}
              >
                <svg width="10" height="16" viewBox="0 0 6 10">
                  <polygon points="5,1 1,5 5,9" fill="#344054" />
                </svg>
              </Box>
              <Box
                sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    height: 16,
                    width: scrollbar.thumbWidth,
                    left: scrollbar.thumbLeft,
                    backgroundColor: "#eaecf0",
                    borderRadius: "10px",
                    cursor: "grab",
                    touchAction: "none",
                    "&:active": { cursor: "grabbing" },
                  }}
                  ref={thumbRef}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onThumbDragStart(e.clientX);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    onThumbDragStart(e.touches[0].clientX);
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: SCROLL_ARROW_W,
                  height: SCROLL_ARROW_W,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                onMouseDown={() => scrollByAmount(100)}
                onTouchStart={(e) => {
                  e.preventDefault();
                  scrollByAmount(100);
                }}
              >
                <svg width="10" height="16" viewBox="0 0 6 10">
                  <polygon points="1,1 5,5 1,9" fill="#344054" />
                </svg>
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export const AssesmentReportTable: React.FC<AssesmentReportTableProps> = ({
  attempts,
}) => {
  const borderColor = "#EAECF0";
  const headerBg = "#F9FAFB";

  const [expandedAttempts, setExpandedAttempts] = useState<Set<string>>(
    new Set(attempts.map((a) => a.userAssignmentAssessmentUid)),
  );

  const toggleAttempt = (uid: string) => {
    setExpandedAttempts((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  if (attempts.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0px 1px 3px 0px #1018281A",
          p: 3,
          height: 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="#475467" fontSize={14} align="center">
          No data available.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack direction="column" gap={2}>
      {attempts.map((attempt) => (
        <AttemptCard
          key={attempt.attemptNumber}
          attempt={attempt}
          isExpanded={expandedAttempts.has(attempt.userAssignmentAssessmentUid)}
          onToggle={() => toggleAttempt(attempt.userAssignmentAssessmentUid)}
          borderColor={borderColor}
          headerBg={headerBg}
        />
      ))}
    </Stack>
  );
};
